/**
 * Standing text as documents (RFC 0334 3g).
 *
 * WHY A DOCUMENT. The soul file (`WorldDoc.agentsMd`) and a project's
 * instructions (`ProjectDoc.instructions`) were strings on the World and the
 * project, so they could only be edited in a form of their own. A person edits
 * a skill in the editor, and these sit beside the skills in the same folder, so
 * they are documents too: `agentsMdDocId` and `instructionsDocId`.
 *
 * THE STRING IS READ UNTIL THE FIRST SAVE MOVES IT. `ensureStandingDocument`
 * makes the document from the string the first time someone opens it, and from
 * then on the document is the text. Every reader goes through
 * `readStandingText`, so an old World and a moved one read the same.
 *
 * ALWAYS ON. What makes these different from a skill is not where they live or
 * how they are edited but that every turn carries them (3b).
 */

import type {
  AnyDocumentId,
  DocHandle,
  Repo,
} from '@automerge/automerge-repo/slim'
import { moveToProject } from '../documents/move-project'
import {
  createDocument,
  moveDocument,
  writeDocumentContent,
} from '../documents/mutations'
import { readDocumentContent } from '../documents/reads'
import { changeProject, projectString } from '../project/scalars'
import type { ProjectDoc } from '../project/types'
import type { WorldDoc } from './types'

export type StandingLevel = 'world' | 'project'

/**
 * `seed` is the string as it stood when the document took over. A writer that
 * does not know about the document (a client that has not synced the pointer
 * yet, or one built before it) can only write the string; when the string no
 * longer matches the seed, that write is newer than the document and wins.
 */
const FIELDS = {
  world: { text: 'agentsMd', docId: 'agentsMdDocId', seed: 'agentsMdSeed' },
  project: {
    text: 'instructions',
    docId: 'instructionsDocId',
    seed: 'instructionsSeed',
  },
} as const

/** What a standing document is called where it is listed. */
export const STANDING_TITLE: Record<StandingLevel, string> = {
  world: 'Soul',
  project: "This project's instructions",
}

/**
 * RFC 0357 3i: once a project's instructions document has reached the root,
 * the instructions are whatever document sits at `AGENTS.md`, as a filesystem
 * would have it. Dragged elsewhere, it stops being served and nothing moves it
 * back. Before this flag, the old id still names them until they are placed.
 */
const PLACED = 'instructionsPlaced'

function isPlaced(doc: unknown): boolean {
  return (doc as Record<string, unknown> | null | undefined)?.[PLACED] === true
}

/**
 * RFC 0357 3i for the World: once placed, the soul file is the `AGENTS.md` at
 * the World's root, which is the home project's root. Dragged away, it is not
 * served; the home project then serves no project instructions of its own,
 * since its root file is the World's.
 */
const WORLD_PLACED = 'agentsMdPlaced'

function isWorldPlaced(doc: unknown): boolean {
  return (
    (doc as Record<string, unknown> | null | undefined)?.[WORLD_PLACED] === true
  )
}

function homeOf(world: unknown): string | undefined {
  const id = (world as WorldDoc | null | undefined)?.homeProjectId
  return id ? String(id).replace(/^automerge:/, '') : undefined
}

/** True when this project's root `AGENTS.md` is the World's soul file. */
export function isWorldSoulHome(world: unknown, projectId: string): boolean {
  return isWorldPlaced(world) && homeOf(world) === projectId
}

/** The document at a project's root `AGENTS.md`, if one is there. */
function rootAgentsDocId(doc: unknown): string | undefined {
  const documents = (doc as ProjectDoc | null | undefined)?.documents ?? {}
  for (const [id, ref] of Object.entries(documents))
    if (projectString(ref?.path) === 'AGENTS.md') return id
  return undefined
}

/** The standing document's id on a World or project doc, or undefined. */
export function standingDocIdOf(
  doc: unknown,
  level: StandingLevel,
): string | undefined {
  if (level === 'project' && isPlaced(doc)) return rootAgentsDocId(doc)
  // A placed World's soul file is an ordinary document at the root: no id
  // names it, so nothing hides it or draws it as a special row.
  if (level === 'world' && isWorldPlaced(doc)) return undefined
  const v = (doc as Record<string, unknown> | null | undefined)?.[
    FIELDS[level].docId
  ]
  return v ? String(v).replace(/^automerge:/, '') : undefined
}

/** The pre-document string, '' when unset. Off the log it may be an
 *  `ImmutableString`, so it is stringified rather than type-checked. A placed
 *  project has no string: its instructions are the root document or nothing. */
function standingStringOf(doc: unknown, level: StandingLevel): string {
  if (level === 'project' && isPlaced(doc)) return ''
  if (level === 'world' && isWorldPlaced(doc)) return ''
  const v = (doc as Record<string, unknown> | null | undefined)?.[
    FIELDS[level].text
  ]
  return v == null ? '' : String(v)
}

/**
 * True when the string was written after the document took over, by a writer
 * that did not know about the document: then the string is the newer text.
 * A document made before seeds existed has none, and keeps the document.
 */
function stringIsNewer(doc: unknown, level: StandingLevel): boolean {
  if (level === 'project' && isPlaced(doc)) return false
  if (level === 'world' && isWorldPlaced(doc)) return false
  const seed = (doc as Record<string, unknown> | null | undefined)?.[
    FIELDS[level].seed
  ]
  return seed != null && String(seed) !== standingStringOf(doc, level)
}

/**
 * The standing text of a World or project doc: its document's content when it
 * has one, else the string. Bounded: a document that does not arrive in time
 * reads as the string, so a slow sync costs freshness, not the turn.
 */
export async function readStandingText(
  repo: Repo,
  doc: unknown,
  level: StandingLevel,
  timeoutMs = 5000,
): Promise<string> {
  if (level === 'world' && isWorldPlaced(doc))
    return readWorldRoot(repo, doc, timeoutMs)
  const docId = standingDocIdOf(doc, level)
  if (!docId || stringIsNewer(doc, level)) return standingStringOf(doc, level)
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const text = await Promise.race([
      (async () => {
        const h = await repo.find(docId as AnyDocumentId)
        await (h as { whenReady?: () => Promise<void> }).whenReady?.()
        return readDocumentContent(h.doc())
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    return text ?? standingStringOf(doc, level)
  } catch {
    return standingStringOf(doc, level)
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** A write to the owner. A project's strings are scalars (RFC 0357 S11); the
 *  World is left as it was written. */
function changeOwner(
  owner: DocHandle<WorldDoc | ProjectDoc>,
  level: StandingLevel,
  fn: (d: WorldDoc | ProjectDoc) => void,
): void {
  if (level === 'project') changeProject(owner, fn)
  else owner.change(fn)
}

/** Where a standing level lives: the World's doc, or the project's. */
function ownerId(
  level: StandingLevel,
  at: { worldId: string; projectId: string },
): string {
  return level === 'world' ? at.worldId : at.projectId
}

/**
 * The standing document for this level, made from the string the first time.
 *
 * A World has no documents of its own, so its soul file is catalogued in the
 * project it was first opened from, as a World skill's document is. It is
 * still the World's: the World doc holds the id.
 */
export async function ensureStandingDocument(
  repo: Repo,
  level: StandingLevel,
  at: { worldId: string; projectId: string },
): Promise<string> {
  const owner = await repo.find<WorldDoc | ProjectDoc>(
    ownerId(level, at) as AnyDocumentId,
  )
  await (owner as { whenReady?: () => Promise<void> }).whenReady?.()
  if (level === 'world' && isWorldPlaced(owner.doc()))
    return ensureWorldRoot(repo, owner.doc())
  let id = standingDocIdOf(owner.doc(), level)
  if (!id) {
    const seed = standingStringOf(owner.doc(), level)
    const created = await createDocument(repo, at.projectId, {
      path: level === 'world' ? 'Skills/Soul.md' : 'AGENTS.md',
      mime: 'text/markdown',
      title: STANDING_TITLE[level],
      content: seed,
    })
    const mine = String(created.documentId)
    changeOwner(owner, level, (d) => {
      const rec = d as unknown as Record<string, unknown>
      // Opened twice at once: the first id to land wins. The other document
      // keeps a copy nobody points at, and is never read.
      if (rec[FIELDS[level].docId]) return
      rec[FIELDS[level].docId] = mine
      rec[FIELDS[level].seed] = seed
    })
    id = standingDocIdOf(owner.doc(), level) ?? mine
  }
  // A save that reached the string after the document was made (another
  // window, an older client) is newer than the document: carry it in, so
  // the editor shows what agents are served.
  if (stringIsNewer(owner.doc(), level)) {
    const latest = standingStringOf(owner.doc(), level)
    await writeIntoDocument(repo, id, latest)
    changeOwner(owner, level, (d) => {
      ;(d as unknown as Record<string, unknown>)[FIELDS[level].seed] = latest
    })
  }
  if (level === 'project' && !isPlaced(owner.doc())) {
    // The one move to the root, for instructions made before it was their
    // home. After it, where the document goes is the person's to decide.
    const project = owner.doc() as ProjectDoc | undefined
    if (projectString(project?.documents?.[id]?.path) !== 'AGENTS.md')
      await moveDocument(repo, at.projectId, id, 'AGENTS.md')
    changeOwner(owner, level, (d) => {
      ;(d as unknown as Record<string, unknown>)[PLACED] = true
    })
  }
  return id
}

/** An existing project instruction document moves to the root in place. */
export async function ensureProjectStandingPath(
  repo: Repo,
  worldId: string,
  projectId: string,
): Promise<string | null> {
  const project = await repo.find<ProjectDoc>(projectId as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  // Placed: nothing to move. The root document, or none, is the answer.
  if (isPlaced(project.doc()))
    return standingDocIdOf(project.doc(), 'project') ?? null
  if (
    !standingDocIdOf(project.doc(), 'project') &&
    !standingStringOf(project.doc(), 'project').trim()
  )
    return null
  return ensureStandingDocument(repo, 'project', { worldId, projectId })
}

/** The text of the `AGENTS.md` at a placed World's root, '' when none. */
async function readWorldRoot(
  repo: Repo,
  world: unknown,
  timeoutMs: number,
): Promise<string> {
  const home = homeOf(world)
  if (!home) return ''
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    const text = await Promise.race([
      (async () => {
        const h = await repo.find(home as AnyDocumentId)
        await (h as { whenReady?: () => Promise<void> }).whenReady?.()
        const id = rootAgentsDocId(h.doc())
        if (!id) return ''
        const d = await repo.find(id as AnyDocumentId)
        await (d as { whenReady?: () => Promise<void> }).whenReady?.()
        return readDocumentContent(d.doc())
      })(),
      new Promise<null>((resolve) => {
        timer = setTimeout(() => resolve(null), timeoutMs)
        ;(timer as { unref?: () => void }).unref?.()
      }),
    ])
    return text ?? ''
  } catch {
    return ''
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/** The World root's `AGENTS.md`, made empty when a placed World has none. */
async function ensureWorldRoot(repo: Repo, world: unknown): Promise<string> {
  const home = homeOf(world)
  if (!home) throw new Error('this World has no root project')
  const h = await repo.find<ProjectDoc>(home as AnyDocumentId)
  await (h as { whenReady?: () => Promise<void> }).whenReady?.()
  const existing = rootAgentsDocId(h.doc())
  if (existing) return existing
  const created = await createDocument(repo, home, {
    path: 'AGENTS.md',
    mime: 'text/markdown',
    title: STANDING_TITLE.world,
    content: '',
  })
  return String(created.documentId)
}

/**
 * Put the World's soul file at the World root once (RFC 0357 3i). Moves the
 * existing soul document there, or makes one from the old string. Leaves the
 * World as it is when the root already holds a different `AGENTS.md`: two
 * files cannot share a path, and choosing between them is a person's call.
 */
export async function ensureWorldSoulPath(
  repo: Repo,
  worldId: string,
): Promise<string | null> {
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const doc = world.doc()
  if (isWorldPlaced(doc)) return rootAgentsDocIdOfHome(repo, doc)
  const home = homeOf(doc)
  if (!home) return null
  const homeHandle = await repo.find<ProjectDoc>(home as AnyDocumentId)
  await (homeHandle as { whenReady?: () => Promise<void> }).whenReady?.()
  const atRoot = rootAgentsDocId(homeHandle.doc())
  const soul = standingDocIdOf(doc, 'world')
  if (atRoot && atRoot !== soul) return null
  let id = atRoot
  if (!id && soul) {
    const target = await repo.find(soul as AnyDocumentId)
    await (target as { whenReady?: () => Promise<void> }).whenReady?.()
    const from = String(
      (target.doc() as { workspaceId?: unknown } | undefined)?.workspaceId ??
        '',
    )
    if (from === home) await moveDocument(repo, home, soul, 'AGENTS.md')
    else
      await moveToProject(
        repo,
        from,
        home,
        { documentId: soul, path: 'AGENTS.md' },
        { worldId },
      )
    id = soul
  }
  if (!id && standingStringOf(doc, 'world').trim()) {
    const created = await createDocument(repo, home, {
      path: 'AGENTS.md',
      mime: 'text/markdown',
      title: STANDING_TITLE.world,
      content: standingStringOf(doc, 'world'),
    })
    id = String(created.documentId)
  }
  world.change((d) => {
    ;(d as unknown as Record<string, unknown>)[WORLD_PLACED] = true
  })
  return id ?? null
}

async function rootAgentsDocIdOfHome(
  repo: Repo,
  world: unknown,
): Promise<string | null> {
  const home = homeOf(world)
  if (!home) return null
  const h = await repo.find<ProjectDoc>(home as AnyDocumentId)
  await (h as { whenReady?: () => Promise<void> }).whenReady?.()
  return rootAgentsDocId(h.doc()) ?? null
}

/** Write a standing document's text, in the project that catalogues it. */
async function writeIntoDocument(
  repo: Repo,
  docId: string,
  text: string,
): Promise<void> {
  const target = await repo.find(docId as AnyDocumentId)
  await (target as { whenReady?: () => Promise<void> }).whenReady?.()
  const projectId = String(
    (target.doc() as { workspaceId?: unknown } | undefined)?.workspaceId ?? '',
  )
  await writeDocumentContent(repo, projectId, docId, text)
}

/**
 * Replace a level's standing text wholesale. Into its document when it has one,
 * so the text an agent is served and the text in the editor are one text; into
 * the string when it does not, which keeps a World nobody has opened yet as it
 * was.
 */
export async function writeStandingText(
  repo: Repo,
  level: StandingLevel,
  ownerDocId: string,
  text: string,
): Promise<void> {
  const owner = await repo.find<WorldDoc | ProjectDoc>(
    ownerDocId as AnyDocumentId,
  )
  await (owner as { whenReady?: () => Promise<void> }).whenReady?.()
  // A placed project with no root AGENTS.md reads no string: writing makes the
  // root document, as saving a new file would.
  if (level === 'world' && isWorldPlaced(owner.doc())) {
    await writeIntoDocument(
      repo,
      await ensureWorldRoot(repo, owner.doc()),
      text,
    )
    return
  }
  const docId =
    level === 'project' &&
    isPlaced(owner.doc()) &&
    !standingDocIdOf(owner.doc(), level)
      ? await ensureStandingDocument(repo, level, {
          worldId: '',
          projectId: ownerDocId,
        })
      : standingDocIdOf(owner.doc(), level)
  if (docId) {
    await writeIntoDocument(repo, docId, text)
    // The string and its seed follow, equal, so a reader that only knows the
    // string sees this text and no reader takes the string as newer.
    changeOwner(owner, level, (d) => {
      const rec = d as unknown as Record<string, unknown>
      rec[FIELDS[level].text] = text
      rec[FIELDS[level].seed] = text
    })
    return
  }
  changeOwner(owner, level, (d) => {
    const rec = d as unknown as Record<string, unknown>
    const field = FIELDS[level].text
    if (text) rec[field] = text
    else delete rec[field]
  })
}
